import { Product as FormProduct } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { ProductQuantityType } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { calculateProductByQuantityMode } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/helpers/calculateProduct';

interface InventoryListPrice {
  averagePricePerUnit?: number;
  averagePricePerUnitUnit?: string;
}

type ListPriceFetcher = (
  productId: string
) => Promise<InventoryListPrice | null>;

/**
 * A product is considered to have a price entered on the source record only
 * when it is a number greater than zero. A `$0` (or missing) price means the
 * user never set one on the Recommendation/Plan/Invoice, so it should be
 * treated as "not set".
 */
const hasEnteredPrice = (product: FormProduct): boolean =>
  typeof product.priceValue === 'number' && product.priceValue > 0;

/**
 * Seeds each product with its saved inventory list price when no price was
 * entered on the source record. Recommendations never carry a price, and
 * Plans/Invoices may come through with `$0` when the user left the cost blank;
 * in both cases we fall back to the product's list price (its default
 * behaviour) and recompute the total cost so the products table and footer
 * populate at creation time without the user having to click "Reset to List
 * Price".
 *
 * Products that already have an entered price are left untouched (the entered
 * price is retained), as are products without a saved list price.
 */
// eslint-disable-next-line import/prefer-default-export
export const enrichProductsWithListPrice = async (
  products: FormProduct[],
  getListPrice: ListPriceFetcher,
  convertFn?: Function
): Promise<FormProduct[]> => {
  return Promise.all(
    products.map(async product => {
      // Retain a price the user explicitly entered on the source record
      if (!product.id || hasEnteredPrice(product)) {
        return product;
      }

      const inventory = await getListPrice(product.id);
      const listPrice = inventory?.averagePricePerUnit;
      const listPriceUnit = inventory?.averagePricePerUnitUnit;

      // No saved list price -> keep default behaviour (cost stays empty)
      if (!listPrice) {
        return product;
      }

      const withListPrice: FormProduct = {
        ...product,
        averagePricePerUnit: listPrice,
        averagePricePerUnitUnit: listPriceUnit,
        priceValue: listPrice,
        priceUnit: listPriceUnit || product.priceUnit
      };

      // In `totalProduct` mode (used by the invoice flow) the total cost is
      // area-independent (cost = totalProduct * price), but
      // `calculateProductValuesFromTotal` short-circuits to `null` when the
      // applied area is <= 0. Invoice-mapped products don't carry an applied
      // area at creation time, so fall back to a harmless positive area for
      // that mode only. This is used solely to derive the cost here and is not
      // persisted (only `totalCost` is read back below). Rate-based modes are
      // left as-is: their cost genuinely depends on area, so without one the
      // cost should stay empty rather than be computed against a fake area.
      const isTotalProductMode = withListPrice.quantityMode === 'totalProduct';
      const areaForCalculation =
        withListPrice.appliedAreaValue || (isTotalProductMode ? 1 : 0);

      const calculated = calculateProductByQuantityMode(
        withListPrice.quantityMode as keyof typeof ProductQuantityType,
        areaForCalculation,
        withListPrice,
        {},
        convertFn
      );

      return {
        ...withListPrice,
        totalCost:
          calculated?.totalCostAtTimeOfCreation ?? withListPrice.totalCost
      };
    })
  );
};
